import { DestroyRef, Directive, afterNextRender, inject, output, signal } from '@angular/core';

@Directive()
export abstract class MdwAlertNotificationBase {
  public animationState = signal<'animated' | 'not-animated'>('not-animated');
  public dismiss = output<void>();

  // In the app an alert lives until the host disposes it in response to `dismiss`, so these
  // timers always fire before the component is destroyed. In unit tests the fixture is destroyed
  // as soon as the test ends; a still-pending timer would then emit on a destroyed OutputRef
  // (NG0953). Track pending timers and clear them on destroy. No behavioural change in production.
  private destroyed = false;
  private readonly pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.pendingTimeouts.forEach(handle => clearTimeout(handle));
      this.pendingTimeouts.clear();
    });

    afterNextRender(() =>
      this.scheduleTimeout(() => {
        this.animationState.set('animated');
      }, 200),
    );
  }

  public close() {
    this.animationState.set('not-animated');
    this.scheduleTimeout(() => this.dismiss.emit(), 200);
  }

  private scheduleTimeout(callback: () => void, delayMs: number) {
    const handle = setTimeout(() => {
      this.pendingTimeouts.delete(handle);
      if (this.destroyed) {
        return;
      }

      callback();
    }, delayMs);
    this.pendingTimeouts.add(handle);
  }
}
